<script setup lang="ts">
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import apiInstance from '@/utils/api'
import { BaseButton, BaseInput, BaseModalClose } from '@/components/base'

const { t } = useI18n()

interface Props {
  isOpen: boolean
}

interface Emits {
  close: []
}

defineProps<Props>()

const emit = defineEmits<Emits>()

const currentPassword = ref('')
const newPassword = ref('')
const confirmPassword = ref('')
const isLoading = ref(false)
const successMessage = ref('')
const errorMessage = ref('')
const fieldErrors = ref<Record<string, string[]>>({})
const showOldPassword = ref(false)
const showNewPassword = ref(false)

const resetForm = () => {
  currentPassword.value = ''
  newPassword.value = ''
  confirmPassword.value = ''
  successMessage.value = ''
  errorMessage.value = ''
  fieldErrors.value = {}
}

const handleCancel = () => {
  resetForm()
  emit('close')
}

const handleSave = async () => {
  // Clear previous messages
  successMessage.value = ''
  errorMessage.value = ''
  fieldErrors.value = {}

  // Basic validation
  if (!currentPassword.value || !newPassword.value || !confirmPassword.value) {
    errorMessage.value = t('changePassword.allFieldsRequired')
    return
  }

  if (newPassword.value !== confirmPassword.value) {
    fieldErrors.value.confirm_password = ['The confirm password field must match new password.']
    return
  }

  isLoading.value = true

  try {
    const response = await apiInstance.post('/change-password', {
      current_password: currentPassword.value,
      new_password: newPassword.value,
      confirm_password: confirmPassword.value,
    })

    if (response.data.error === false) {
      successMessage.value = response.data.message || t('changePassword.success')

      // Close modal after 2 seconds
      setTimeout(() => {
        resetForm()
        emit('close')
      }, 2000)
    } else {
      // Handle validation errors
      if (response.data.errors && typeof response.data.errors === 'object') {
        fieldErrors.value = response.data.errors
        errorMessage.value = response.data.message || t('changePassword.validationError')
      } else {
        errorMessage.value = response.data.message || t('changePassword.error')
      }
    }
  } catch (error: any) {
    console.error('Error changing password:', error)

    // Handle API errors
    if (error.response?.status === 422 && error.response?.data?.errors) {
      fieldErrors.value = error.response.data.errors
      errorMessage.value = error.response.data.message || t('changePassword.validationError')
    } else if (error.response?.data?.message) {
      errorMessage.value = error.response.data.message
    } else {
      errorMessage.value = error.message || t('changePassword.error')
    }
  } finally {
    isLoading.value = false
  }
}

const getFieldError = (fieldName: string): string => {
  const errors = fieldErrors.value[fieldName]
  return errors && errors.length > 0 ? errors[0] : ''
}
</script>

<template>
  <!-- Modal Backdrop -->
  <div
    v-if="isOpen"
    class="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
  >
    <!-- Modal Content -->
    <div
      class="bg-white rounded-lg shadow-lg max-w-md w-full"
      @click.stop
    >
      <!-- Modal Header -->
      <div class="border-b border-gray-200 flex items-center justify-between px-6 py-4">
        <h2 class="text-gray-900 text-xl font-bold">
          {{ t('changePassword.title') }}
        </h2>
        <BaseModalClose @click="handleCancel" />
      </div>

      <!-- Modal Body -->
      <div class="px-6 py-4">
        <!-- Success Message -->
        <div
          v-if="successMessage"
          class="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm"
        >
          {{ successMessage }}
        </div>

        <!-- Error Message -->
        <div
          v-if="errorMessage"
          class="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm"
        >
          {{ errorMessage }}
        </div>

        <!-- Form -->
        <form @submit.prevent="handleSave" class="space-y-4">
          <!-- Current Password Field -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              {{ t('changePassword.currentPassword') }}
            </label>
            <div class="relative">
              <BaseInput
                v-model="currentPassword"
                :type="showOldPassword ? 'text' : 'password'"
                :disabled="isLoading"
                :placeholder="t('changePassword.currentPasswordPlaceholder')"
                :invalid="!!(getFieldError('current_password'))"
              />
              <button
                type="button"
                @click="showOldPassword = !showOldPassword"
                class="absolute right-3 top-2 text-gray-600 hover:text-gray-900"
              >
                <svg v-if="showOldPassword" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6-10-6-10-6z"/>
                  <circle cx="12" cy="12" r="3"/>
                  <line x1="3" y1="21" x2="21" y2="3"/>
                </svg>
              </button>
              <p v-if="getFieldError('current_password')" class="mt-1 text-sm text-red-600">
                {{ getFieldError('current_password') }}
              </p>
            </div>
          </div>

          <!-- New Password Field -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              {{ t('changePassword.newPassword') }}
            </label>
            <div class="relative">
              <BaseInput
                v-model="newPassword"
                :type="showNewPassword ? 'text' : 'password'"
                :disabled="isLoading"
                :placeholder="t('changePassword.newPasswordPlaceholder')"
                :invalid="!!(getFieldError('new_password'))"
              />
              <button
                type="button"
                @click="showNewPassword = !showNewPassword"
                class="absolute right-3 top-2 text-gray-600 hover:text-gray-900"
              >
                <svg v-if="showNewPassword" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6-10-6-10-6z"/>
                  <circle cx="12" cy="12" r="3"/>
                  <line x1="3" y1="21" x2="21" y2="3"/>
                </svg>
              </button>
              <p v-if="getFieldError('new_password')" class="mt-1 text-sm text-red-600">
                {{ getFieldError('new_password') }}
              </p>
            </div>
          </div>

          <!-- Confirm Password Field -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              {{ t('changePassword.confirmPassword') }}
            </label>
            <div class="relative">
              <BaseInput
                v-model="confirmPassword"
                :type="showNewPassword ? 'text' : 'password'"
                :disabled="isLoading"
                :placeholder="t('changePassword.confirmPasswordPlaceholder')"
                :invalid="!!(getFieldError('confirm_password'))"
              />
              <button
                type="button"
                @click="showNewPassword = !showNewPassword"
                class="absolute right-3 top-2 text-gray-600 hover:text-gray-900"
              >
                <svg v-if="showNewPassword" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6-10-6-10-6z"/>
                  <circle cx="12" cy="12" r="3"/>
                  <line x1="3" y1="21" x2="21" y2="3"/>
                </svg>
              </button>
              <p v-if="getFieldError('confirm_password')" class="mt-1 text-sm text-red-600">
                {{ getFieldError('confirm_password') }}
              </p>
            </div>
          </div>
        </form>
      </div>

      <!-- Modal Footer -->
      <div class="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
        <BaseButton
          @click="handleCancel"
          :disabled="isLoading"
          variant="secondary"
        >
          {{ t('common.cancel') }}
        </BaseButton>
        <BaseButton
          @click="handleSave"
          :disabled="isLoading"
        >
          <span v-if="!isLoading">{{ t('common.save') }}</span>
          <span v-else class="flex items-center gap-2">
            <svg class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            {{ t('common.saving') }}
          </span>
        </BaseButton>
      </div>
    </div>
  </div>
</template>
